import { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ParkedRow } from '@shared/types';
import { AttentionFlags } from '@web/components/Attention';
import { Button } from '@web/components/Button';
import { EmptyState, ErrorState, LoadingBar } from '@web/components/Feedback';
import { LiveRegion, useThrottledAnnouncement } from '@web/components/LiveRegion';
import { ReasonChip } from '@web/components/ReasonChip';
import { TemperatureMeter } from '@web/components/TemperatureMeter';
import { WaitTimer } from '@web/components/Timers';
import { cn } from '@web/lib/cn';
import { useClaim } from './useClaim';
import { useParked } from './useParked';

/**
 * The parked queue.
 *
 * A pull queue, not an assignment list: the server returns longest-wait-first
 * and the client does not re-rank it, so every operator is looking at the same
 * idea of "next". Claiming is first-wins on one conditional UPDATE; the loser
 * gets a 409 that names the winner, and the row stays gone.
 */
export function ParkedPage() {
  const parked = useParked();
  const rows = parked.data ?? [];

  const announced = useThrottledAnnouncement(
    rows.length === 0
      ? 'Queue clear.'
      : `${String(rows.length)} conversation${rows.length === 1 ? '' : 's'} waiting.`,
    8000,
  );

  if (parked.isPending) return <LoadingBar label="Loading queue" />;
  if (parked.error !== null) {
    return (
      <ErrorState
        error={parked.error}
        onRetry={() => {
          void parked.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <LiveRegion text={announced} />

      <div className="flex items-baseline gap-3 border-b border-rule bg-panel px-3 py-2">
        <span className="font-mono text-lg leading-none font-medium tabular-nums">
          {rows.length}
        </span>
        <span className="font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase">
          waiting for a human
        </span>
        {rows.length > 0 && (
          <span className="ml-auto font-mono text-[10px] tracking-[0.1em] text-ink-faint uppercase">
            longest wait first
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-auto [overscroll-behavior:contain]">
        {rows.length === 0 ? (
          <EmptyState
            headline="Nobody is waiting."
            detail="The assistant is handling every live conversation. Parked conversations appear here the moment one is escalated."
          />
        ) : (
          <ul>
            {rows.map((row) => (
              <QueueCard key={row.conversationId} row={row} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const QueueCard = memo(function QueueCard({ row }: { row: ParkedRow }) {
  const claim = useClaim();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const summary = row.parkSummary ?? row.lastMessagePreview;

  return (
    <li className="border-b border-rule">
      <div className="flex items-start gap-3 px-3 py-2.5">
        <div className="flex w-16 shrink-0 flex-col items-start gap-1 pt-0.5">
          <WaitTimer since={row.parkedAt} size="large" />
          <span className="font-mono text-[9px] tracking-[0.1em] text-ink-faint uppercase">
            waiting
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="truncate text-sm font-medium text-ink underline-offset-2 hover:underline"
              onClick={() => {
                void navigate(`/thread/${row.conversationId}`);
              }}
            >
              {row.candidateName}
            </button>
            <span className="font-mono text-[11px] text-ink-faint tabular-nums">{row.phone}</span>
            <span className="text-[11px] text-ink-faint">{row.trade ?? 'trade unknown'}</span>
            <TemperatureMeter temperature={row.temperature} />
            <ReasonChip reason={row.parkReason} size="full" />
          </div>

          {summary !== null && (
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => {
                setExpanded((open) => !open);
              }}
              className={cn(
                'mt-1 w-full text-left text-xs text-ink-dim',
                expanded ? '' : 'line-clamp-2',
              )}
            >
              {summary}
            </button>
          )}

          {row.attentionFlags.length > 0 && (
            <AttentionFlags flags={row.attentionFlags} limit={5} className="mt-1.5" />
          )}
        </div>

        <div className="shrink-0 pt-0.5">
          <Button
            tone="primary"
            size="touch"
            // Double-taps on a phone otherwise fire two claims; the second one
            // loses to the first and the same person is told they lost.
            disabled={claim.isPending}
            onClick={() => {
              claim.mutate(row.conversationId);
            }}
          >
            {claim.isPending ? 'Claiming…' : 'Claim'}
          </Button>
        </div>
      </div>
    </li>
  );
});
